/** Shared "come back to where you were" plumbing for the auth screens. */

interface PathLike {
  pathname: string;
  search?: string;
  state?: unknown;
}

// Auth screens are never a valid destination — returning to one loops.
const AUTH_PATHS = new Set([
  '/login',
  '/signup',
  '/verify-otp',
  '/forgot-password',
  '/complete-profile',
]);

/** Where an authenticated user lands when there is nowhere to return to. */
export const DEFAULT_AFTER_LOGIN = '/history';

// Same-site paths only — rejects `//host`, absolute URLs and backslash tricks
// so a crafted link cannot turn login into an open redirect.
export function sanitizeReturnTo(raw: unknown): string | undefined {
  return typeof raw === 'string' && /^\/(?![/\\])/.test(raw) ? raw : undefined;
}

/** The current page, if it is somewhere worth coming back to. */
export function captureReturnTo(location: PathLike): string | undefined {
  if (AUTH_PATHS.has(location.pathname)) return undefined;
  return sanitizeReturnTo(`${location.pathname}${location.search || ''}`);
}

/** A query param survives a refresh and a hard redirect; router state does not. */
export function withReturnTo(path: string, returnTo?: string): string {
  return returnTo ? `${path}?returnTo=${encodeURIComponent(returnTo)}` : path;
}

export function loginPath(location: PathLike): string {
  return withReturnTo('/login', captureReturnTo(location));
}

export function signupPath(location: PathLike): string {
  return withReturnTo('/signup', captureReturnTo(location));
}

/** State wins over the query param — only state can carry a payload (e.g. `service`). */
export function resolveReturnTo(location: PathLike): string | undefined {
  const fromState = sanitizeReturnTo((location.state as { returnTo?: unknown } | null)?.returnTo);
  if (fromState) return fromState;
  return sanitizeReturnTo(new URLSearchParams(location.search || '').get('returnTo'));
}
